import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    ceil,
    cloneDeep,
    filter,
    find,
    map,
    max,
    min,
    some,
    split, toNumber,
} from "lodash";
import {
  Box,
  Grid,
  Pagination,
  Typography,
  Button,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useArgusCart } from "../../contexts/ArgusCartContext";
import GridProductsItem from "../GridProductsItem";
import { formatCategoryName, toMap } from "../../utils/helpers";
import { COLORS } from "../../constants";
import useSystemHook from "../../hooks/SystemHook";
import { ScreenSize } from "../../utils/constants";

export const GridProducts = ({
  products,
  pricingData,
  stockInfo,
  customerCategory,
  filters,
  pageNumber,
  pageSize,
  handlePageNumberChange,
  totalResults,
  itemsPerRow,
  toggleFilterDrawer,
  categoriesAndTypes,
}) => {
  const [pricingDataDictionary, setPricingDataDictionary] = useState(null);
  const [stockInfoDataDictionary, setStockInfoDataDictionary] = useState(null);
  const [showOnlyInStock, setShowOnlyInStock] = useState(() => {
    const saved = localStorage.getItem("showOnlyInStock");
    return saved ? JSON.parse(saved) : false;
  });

  const { items, setItems } = useArgusCart();
  const [showVehicleSpecificInformation, setShowVehicleSpecificInformation] =
    useState(false);

  const location = useLocation();
  const { screenSize } = useSystemHook();

  const { isMobile, isTablet, isDesktop } = useSystemHook();

  const scrollTop = () => {
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    scrollTop();
  }, []);

  useEffect(() => {
    if (Boolean(pricingData)) {
      const result = toMap(pricingData, (x) => x.sku);
      setPricingDataDictionary(result);
    }
  }, [pricingData]);

  useEffect(() => {
    if (Boolean(stockInfo)) {
      const result = toMap(stockInfo, (x) => x.sku);
      setStockInfoDataDictionary(result);
    }
  }, [stockInfo]);

  useEffect(() => {
    if (!!filters) {
      const manufacturers = filter(
        filters,
        (x) => x.type === "MANUFACTURER"
      )[0];
      const models = filter(filters, (x) => x.type === "MODEL")[0];
      const variants = filter(filters, (x) => x.type === "VARIANT")[0];
      const years = filter(filters, (x) => x.type === "YEAR")[0];

      const anyActiveManufacturers = some(
        manufacturers?.values,
        (x) => x.active
      );
      const anyActiveModels = some(models?.values, (x) => x.active);
      const anyActiveVariants = some(variants?.values, (x) => x.active);
      const anyActiveYears = some(years?.values, (x) => x.active);

      setShowVehicleSpecificInformation(
        anyActiveManufacturers &&
          anyActiveModels &&
          anyActiveVariants &&
          anyActiveYears
      );
    }
  }, []);

  const handleUpdateQuantity = (productId, quantity) => {
    const itemsCopy = cloneDeep(items);
    const itemToUpdate = find(itemsCopy, (x) => x.id === productId);
    itemToUpdate.quantity = quantity;
    setItems(itemsCopy);
  };

  // Get the active category from filters
  const pathParts = split(location.pathname, "/");
  const activeCategory = find(categoriesAndTypes, x => x.productCategoryId === toNumber(pathParts[2]))?.productCategoryName;

  useEffect(() => {
    localStorage.setItem("showOnlyInStock", JSON.stringify(showOnlyInStock));
  }, [showOnlyInStock]);

  const filteredProducts = useMemo(() => {
    if (!products || !Array.isArray(products)) return [];

    if (!showOnlyInStock) return products;

    return products.filter((product) => {
      const stockObjects = stockInfoDataDictionary?.get(product.sku);
      const stockObj = stockObjects?.[0];
      const availability =
        stockObj?.quantityOnHand ?? product.availability ?? "";
      const status = availability?.toLowerCase();
      return status === "in stock" || status === "low stock";
    });
  }, [products, showOnlyInStock, stockInfoDataDictionary]);

  // filteredProducts.length when filter is ON
  // totalResults when filter is OFF (normal behaviour)

  const effectiveTotal = showOnlyInStock
    ? filteredProducts.length
    : totalResults;

  // Calculate the start and end indices for the current page
  const currentList = showOnlyInStock ? filteredProducts : products;

  const maxPage = ceil(effectiveTotal / pageSize);
  const safePageNumber = Math.min(pageNumber, maxPage || 1);

  const startIndex =
    effectiveTotal > 0 ? Math.max(1, (safePageNumber - 1) * pageSize + 1) : 0;

  const endIndex =
    effectiveTotal > 0
      ? Math.min(startIndex + currentList.length - 1, effectiveTotal)
      : 0;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 1,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: isMobile ? 500 : 600,
            color: COLORS.darkText,
            textTransform: "uppercase",
            fontSize: "var(--font-size-l)",
          }}
        >
          {formatCategoryName(activeCategory)}
        </Typography>

        {/* SWITCH: SHOW ONLY IN STOCK */}
        {/* <FormControlLabel
          control={
            <Switch
              checked={showOnlyInStock}
              onChange={(e) => setShowOnlyInStock(e.target.checked)}
            />
          }
          label="Show only in stock"
        /> */}
      </Box>

      {/* Filter Button ONLY SHOWING FOR MOBILE*/}
      {screenSize === ScreenSize.Mobile && (
        <Grid item xs={12}>
          <Button
            variant="outlined"
            onClick={toggleFilterDrawer}
            sx={{
              my: 2,
              borderRadius: "2px",
              backgroundColor: COLORS.white,
              fontSize: "var(--font-size-xxs)",
              border: `1px solid ${COLORS.border}`,
              color: COLORS.darkText,
            }}
          >
            Filter
          </Button>
        </Grid>
      )}

      <Typography
        sx={{
          my: 2,
          color: COLORS.lightText,
          fontSize: isMobile ? "var(--font-size-xxs)" : "var(--font-size-s)",
        }}
      >
        {startIndex} - {endIndex} of {effectiveTotal}
      </Typography>
      <Grid container spacing={{ xs: 1, md: 2 }}>
        {!!products &&
          !!pricingData &&
          !!pricingDataDictionary &&
          !!stockInfoDataDictionary &&
          map(filteredProducts, (product, itemNumber) => {
            const priceObject = pricingDataDictionary.get(product.sku);
            const stockInfoObject = stockInfoDataDictionary.get(product.sku);

            if (!priceObject) {
              console.warn(`Missing pricing data for product: ${product.sku}`);
              return null;
            }

            const promoPrice = priceObject[0].promo ?? 0;
            const promoInvoicePrice = priceObject[0].promoInvoicePrice ?? 0;

            let itemPrice = priceObject[0].price ?? 0;
            let invoicePrice = priceObject[0].invoicePrice ?? 0;

            let availability = product.availability;
            if (!!stockInfoObject){
                availability = stockInfoObject[0].quantityOnHand;
            }

            return (
              <Grid
                item
                xs={isMobile ? 6 : 12}
                sm={6}
                lg={12 / itemsPerRow}
                key={itemNumber}
              >
                <GridProductsItem
                  key={itemNumber}
                  title={product.name}
                  price={itemPrice}
                  invoicePrice={invoicePrice}
                  promo={promoPrice}
                  promoInvoicePrice={promoInvoicePrice}
                  customerCategory={customerCategory}
                  vendor={product.vendor}
                  image={product.image}
                  productId={product.productId}
                  vendorImage={product.vendorImage}
                  handle={product.handle}
                  vendorDescription={product.vendorDescription}
                  quality={product.quality}
                  fitment={product.fitment}
                  availability={availability}
                  warranty={product.warranty}
                  warrantyText={product.warrantyText}
                  applicationPosition={product.applicationPosition}
                  applicationPositionImage={product.applicationPositionImage}
                  bodyHtml={product.html}
                  sku={product.sku}
                  updateCart={handleUpdateQuantity}
                />
              </Grid>
            );
          })}
      </Grid>

      <Grid container justifyContent="center" sx={{ mt: 7 }}>
        <Grid item>
          {totalResults && totalResults > 0 && (
            <Pagination
              count={ceil(effectiveTotal / pageSize)}
              page={pageNumber}
              onChange={handlePageNumberChange}
              size={isMobile ? "small" : "large"}
              variant="outlined"
              shape="rounded"
              sx={{
                "& .MuiPaginationItem-page": {
                  p: 3,
                  fontSize: isMobile
                    ? "var(--font-size-xxs)"
                    : "var(--font-size-s)",
                  border: "none !important",
                  "&.Mui-selected": {
                    backgroundColor: COLORS.themeRed,
                    color: `${COLORS.white} !important`,
                  },
                  "&:not(.Mui-selected)": {
                    backgroundColor: COLORS.border,
                    color: COLORS.lightText,
                    "&:hover": {
                      backgroundColor: COLORS.border,
                      opacity: 0.8,
                    },
                  },
                },
                "& .MuiPaginationItem-previousNext": {
                  border: "none !important",
                  color: COLORS.lightText,
                  p: 3,
                  fontSize: "1rem",
                  "&:hover": {
                    backgroundColor: COLORS.border,
                    opacity: 0.8,
                  },
                },
                "& .MuiPaginationItem-ellipsis": {
                  backgroundColor: "transparent",
                  color: COLORS.lightText,
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                },
              }}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
